<template>
  <div class="info-box-flex">
    <!-- #region tubing-box -->
    <table class="info-box">
      <thead>
        <tr>
          <th>PRODUCTION TUBING</th>
          <th style="font-size: x-small">
            {{
              format(
                convert(
                  tubingSubtotalWeight,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th style="font-size: x-small">
            {{
              format(
                convert(
                  tubingSubtotalLength,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="scenario.well_properties?.r_top_tubing">
          <td>
            Top Tubing ({{
              computeTubingLabel(
                scenario.well_properties?.r_top_tubing,
                getLargeLengthUnit("Length.write.Feet").value?.label ===
                  "Meters"
              )
            }})
          </td>
          <td>
            {{
              format(
                convert(
                  (scenario.well_properties?.r_top_tubing?.pound_per_foot ??
                    0) *
                    (scenario.well_properties?.top_tubing_length_feet ?? 0),
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </td>
          <td>
            {{
              format(
                convert(
                  scenario.well_properties?.top_tubing_length_feet ?? 0,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }}
          </td>
          <td>
            {{
              formatDepth(
                scenarioCalculations.equipmentLengths.tubingLengthFt.value -
                  (scenario.well_properties?.bottom_tubing_length_feet ?? 0)
              )
            }}
          </td>
        </tr>
        <tr v-if="scenario.well_properties?.r_bottom_tubing">
          <td>
            Bottom Tubing ({{
              computeTubingLabel(
                scenario.well_properties?.r_bottom_tubing,
                getLargeLengthUnit("Length.write.Feet").value?.label ===
                  "Meters"
              )
            }})
          </td>
          <td>
            {{
              format(
                convert(
                  (scenario.well_properties?.r_bottom_tubing?.pound_per_foot ??
                    0) *
                    (scenario.well_properties?.bottom_tubing_length_feet ?? 0),
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </td>
          <td>
            {{
              format(
                convert(
                  scenario.well_properties?.bottom_tubing_length_feet ?? 0,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }}
          </td>
          <td>
            {{
              formatDepth(
                scenarioCalculations.equipmentLengths.tubingLengthFt.value
              )
            }}
          </td>
        </tr>

        <tr
          v-if="
            !scenario.well_properties?.r_top_tubing &&
            !scenario.well_properties?.r_bottom_tubing
          "
        >
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion tubing-box -->

    <!-- #region cables-box -->
    <table class="info-box">
      <thead>
        <tr>
          <th>CABLES</th>
          <th style="font-size: x-small">
            {{
              format(
                convert(
                  scenarioCalculations.cableCalculations.aggregate
                    .totalCablesWeightPounds.value,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th style="font-size: x-small">
            {{
              format(
                convert(
                  scenarioCalculations.cableCalculations.aggregate
                    .totalCablesLengthFt.value,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(c, i) in scenarioCalculations.orderedCables.value"
          :key="`cable-row-${i}`"
        >
          <td>
            {{
              scenarioCalculations.cableCalculations.individual.value[c.id]
                .cableHeader
            }}
          </td>
          <td>
            {{
              format(
                convert(
                  scenarioCalculations.cableCalculations.individual.value[c.id]
                    .cableWeightLbs,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </td>
          <td>
            {{
              format(
                convert(
                  c.length_feet,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }}
          </td>
          <td>{{ formatDepth(cableDepthsFt[i]) }}</td>
        </tr>
        <tr v-if="!scenarioCalculations.orderedCables.value.length">
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion cables-box -->

    <!-- #region ytool-box -->
    <table v-if="scenario.equipment?.use_uphole_y_tool" class="info-box">
      <thead>
        <tr>
          <th>Y TOOLS / TUBINGS</th>
          <th style="font-size: x-small">
            {{
              format(
                convert(
                  ytoolTotalWeight,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th style="font-size: x-small">
            <!-- {{
              format(
                convert(
                  ytoolTotalLength,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }} -->
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            Y Block
            {{ computeYToolLabel(scenario.equipment?.r_uphole_y_tool) }}
          </td>
          <td>
            {{
              format(
                convert(
                  scenario.equipment?.r_uphole_y_tool?.weight_pounds ?? 0,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </td>
          <td>
            {{
              format(
                convert(
                  (scenario.equipment?.r_uphole_y_tool?.length_inches ?? 0) /
                    12,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }}
          </td>
          <td>
            {{
              formatDepth(
                scenarioCalculations.equipmentLengths.bottomOfYToolDepthFt.value
              )
            }}
          </td>
        </tr>
        <tr
          v-for="(tubing, i) in scenarioCalculations.orderedYToolBypassTubings
            .value"
          :key="`bypass-tubing-${i}`"
        >
          <td>
            Bypass Tubing ({{
              computeTubingLabel(
                tubing.r_tubing,
                getLargeLengthUnit("Length.write.Feet").value?.label ===
                  "Meters"
              ) || "Bypass Tubing"
            }})
          </td>
          <td>
            {{
              format(
                convert(
                  (tubing.r_tubing?.pound_per_foot ?? 0) *
                    (tubing.length_feet ?? 0),
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </td>
          <td>
            {{
              format(
                convert(
                  tubing.length_feet ?? 0,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }}
          </td>
          <td>{{ formatDepth(bypassTubingDepthsFt[i]) }}</td>
        </tr>
        <tr
          v-for="(tubing, i) in scenarioCalculations
            .orderedYToolEquipmentTubings.value"
          :key="`equipment-tubing-${i}`"
        >
          <td>
            Equip. Tubing ({{
              computeTubingLabel(
                tubing.r_tubing,
                getLargeLengthUnit("Length.write.Feet").value?.label ===
                  "Meters"
              ) || "Equipment Tubing"
            }})
          </td>
          <td>
            {{
              format(
                convert(
                  (tubing.r_tubing?.pound_per_foot ?? 0) *
                    (tubing.length_feet ?? 0),
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </td>
          <td>
            {{
              format(
                convert(
                  tubing.length_feet ?? 0,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.write.Feet").value?.shortLabel }}
          </td>
          <td>{{ formatDepth(equipmentTubingDepthsFt[i]) }}</td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion ytool-box -->

    <!-- #region discharge-box -->
    <table class="info-box">
      <thead>
        <tr>
          <th>BOLT ON DISCHARGE</th>
          <th>
            {{
              isBoltOnDischargeMeasurementUnknown(
                scenario.equipment?.r_bolt_on_discharge,
                "weight_pounds"
              )
                ? ""
                : format(
                    convert(
                      scenarioCalculations.equipmentWeights
                        .boltOnDischargeWeightLbs.value,
                      MassUnits.Pounds,
                      getMassUnit("Weight.convert.lbs").value
                    )
                  ) + ` ${getMassUnit("Weight.convert.lbs").value?.shortLabel}`
            }}
          </th>
          <th>
            {{
              isBoltOnDischargeMeasurementUnknown(
                scenario.equipment?.r_bolt_on_discharge,
                "length_inches"
              )
                ? ""
                : format(
                    convert(
                      scenarioCalculations.equipmentLengths
                        .boltOnDischargeLengthFt.value,
                      LengthUnits.Feet,
                      getLargeLengthUnit("Length.write.Feet").value
                    )
                  ) +
                  ` ${getLargeLengthUnit("Length.write.Feet").value?.shortLabel}`
            }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="scenario.equipment?.r_bolt_on_discharge">
          <td>
            {{
              computeBoltOnDischargeLabel(
                scenario.equipment?.r_bolt_on_discharge
              )
            }}
          </td>
          <td>
            {{
              isBoltOnDischargeMeasurementUnknown(
                scenario.equipment?.r_bolt_on_discharge,
                "weight_pounds"
              )
                ? ""
                : format(
                    convert(
                      scenario.equipment?.r_bolt_on_discharge?.weight_pounds,
                      MassUnits.Pounds,
                      getMassUnit("Weight.convert.lbs").value
                    )
                  ) + ` ${getMassUnit("Weight.convert.lbs").value?.shortLabel}`
            }}
          </td>
          <td>
            {{
              isBoltOnDischargeMeasurementUnknown(
                scenario.equipment?.r_bolt_on_discharge,
                "length_inches"
              )
                ? ""
                : format(
                    convert(
                      (scenario.equipment?.r_bolt_on_discharge?.length_inches ??
                        0) / 12,
                      LengthUnits.Feet,
                      getLargeLengthUnit("Length.write.Feet").value
                    )
                  ) +
                  ` ${getLargeLengthUnit("Length.write.Feet").value?.shortLabel}`
            }}
          </td>
          <td>
            {{
              formatDepth(
                scenarioCalculations.equipmentLengths.completePumpDepthFt.value
              )
            }}
          </td>
        </tr>

        <tr v-if="!scenario.equipment?.r_bolt_on_discharge">
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion discharge-box -->

    <!-- #region housings-box -->
    <table class="info-box">
      <thead>
        <tr>
          <th>PUMPS / HOUSINGS</th>
          <th>
            {{
              format(
                convert(
                  scenarioCalculations.equipmentWeights.pumpWeightLbs.value,
                  MassUnits.Pounds,
                  getMassUnit("Pump Weight.write.lbs").value
                )
              )
            }}
            {{ getMassUnit("Pump Weight.write.lbs").value?.shortLabel }}
          </th>
          <th>
            {{
              format(
                convert(
                  scenarioCalculations.equipmentLengths.pumpLengthFt.value,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Pump Length.write.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Pump Length.write.Feet").value?.shortLabel }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="{
            housingIndex,
            pumpLabel,
            length,
            lengthLabel,
            weight,
            weightLabel,
            bottomDepthFt
          } in pumpTableData"
          :key="`pump-housing-row-${housingIndex}`"
        >
          <td>
            <strong>P{{ housingIndex + 1 }}</strong>
            {{ pumpLabel }}
          </td>
          <td>{{ weight }} {{ weightLabel }}</td>
          <td>{{ length }} {{ lengthLabel }}</td>
          <td>{{ formatDepth(bottomDepthFt) }}</td>
        </tr>

        <tr v-if="!pumpTableData.length">
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion housings-->

    <!-- #region intakes-box -->
    <table class="info-box">
      <thead>
        <tr>
          <th>INTAKES</th>
          <th>
            {{
              format(
                convert(
                  scenarioCalculations.equipmentWeights.intakeWeightLbs.value,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th>
            {{
              format(
                convert(
                  scenarioCalculations.equipmentLengths.intakeLengthFt.value,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.convert.Inches").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.convert.Inches").value?.shortLabel }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(n, i) in scenarioCalculations.orderedIntakes.value"
          :key="`intake-row-${i}`"
        >
          <td>
            <strong>I{{ i + 1 }}</strong>
            {{ computeIntakeLabel(n.r_intake, n.use_high_strength_shaft) }}
          </td>
          <td>
            {{
              format(
                convert(
                  n.r_intake?.weight_pounds,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </td>
          <td>
            {{
              format(
                convert(
                  n.r_intake?.length_inches,
                  LengthUnits.Inches,
                  getLargeLengthUnit("Length.convert.Inches").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.convert.Inches").value?.shortLabel }}
          </td>
          <td>{{ formatDepth(intakeDepthsFt[i]) }}</td>
        </tr>
        <tr v-if="!scenarioCalculations.orderedIntakes.value.length">
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion intakes-box -->

    <!-- #region shroud-box -->
    <table
      v-if="scenario?.equipment?.r_shroud?.interior_diameter_inches"
      class="info-box mt-2 shroud"
    >
      <thead>
        <tr>
          <th>SHROUD</th>
          <th></th>
          <th></th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td colspan="2">
            {{
              computeCasingLabel(
                scenario.equipment?.r_shroud,
                getLargeLengthUnit("Length.write.Feet").value?.label ===
                  "Meters"
              )
            }}
          </td>
          <td>
            {{
              format(
                convert(
                  scenarioCalculations.equipmentLengths.shroudLengthFt.value,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Equipment Length.convert.Feet").value
                )
              )
            }}
            {{
              getLargeLengthUnit("Equipment Length.convert.Feet").value
                ?.shortLabel
            }}
          </td>
          <td>
            {{
              formatDepth(
                scenarioCalculations.equipmentLengths.bottomOfEquipmentDepthFt
                  .value
              )
            }}
          </td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion shroud-box -->

    <!-- #region seals-box -->
    <table class="info-box">
      <thead>
        <tr>
          <th>{{ sagdMode ? "THRUST" : "SEALS" }}</th>
          <th>
            {{
              format(
                convert(
                  scenarioCalculations.equipmentWeights.sealWeightLbs.value,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th>
            {{
              format(
                convert(
                  scenarioCalculations.equipmentLengths.sealLengthFt.value,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.convert.Inches").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.convert.Inches").value?.shortLabel }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(s, i) in scenarioCalculations.descendingOrderSeals.value"
          :key="`seal-row-${i}`"
        >
          <td>
            <strong>S{{ i + 1 }}</strong>
            {{ computeSealLabel(s.r_seal, s.use_high_strength_shaft) }}
          </td>
          <td>
            {{
              format(
                convert(
                  s.r_seal?.weight_pounds,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </td>
          <td>
            {{
              format(
                convert(
                  s.r_seal?.length_inches,
                  LengthUnits.Inches,
                  getLargeLengthUnit("Length.convert.Inches").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.convert.Inches").value?.shortLabel }}
          </td>
          <td>{{ formatDepth(sealDepthsFt[i]) }}</td>
        </tr>
        <tr v-if="!scenarioCalculations.descendingOrderSeals.value.length">
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion seals-box -->

    <!-- #region motor-box -->
    <table class="info-box">
      <thead>
        <tr>
          <th>MOTOR LEAD EXTENSION</th>
          <th>
            {{
              format(
                convert(
                  scenarioCalculations.equipmentWeights.mleWeightLbs.value,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th>
            {{
              format(
                convert(
                  scenario.equipment?.r_mle?.length_feet,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.convert.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.convert.Feet").value?.shortLabel }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="scenario.equipment?.r_mle">
          <td>
            {{
              scenarioCalculations.motorLeadExtensionCalculations.mleCableData
                .value.cableHeader
            }}
          </td>
          <td></td>
          <td></td>
          <td>
            {{
              formatDepth(
                scenarioCalculations.equipmentLengths.depthToReachMLEPlugFt
                  .value
              )
            }}
          </td>
        </tr>
        <tr v-else>
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <table class="info-box mt-2 motor">
      <thead>
        <tr>
          <th>MOTOR</th>
          <th>
            {{
              format(
                convert(
                  scenario.equipment?.r_motor?.weight_pounds,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th>
            {{
              format(
                convert(
                  scenario.equipment?.r_motor?.length_inches,
                  LengthUnits.Inches,
                  getLargeLengthUnit("Length.convert.Inches").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.convert.Inches").value?.shortLabel }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="scenario.equipment?.r_motor">
          <tr>
            <td>
              {{ computeMotorLabel(scenario.equipment?.r_motor) }}
            </td>
            <td style="text-align: left" colspan="1">Rating</td>
            <td></td>
            <td>
              {{
                formatDepth(
                  scenarioCalculations.equipmentLengths.bottomOfMotorDepthFt
                    .value
                )
              }}
            </td>
          </tr>
          <tr v-for="s in motorStats" :key="s.field">
            <td class="pl-3">{{ s.field }}</td>
            <td style="text-align: left" colspan="3">
              {{ s.rerate }} ({{ s.nameplate }})
            </td>
          </tr>
        </template>
        <tr v-else>
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>

    <!-- #endregion motor-box -->

    <!-- #region sensor-box -->
    <table class="info-box sensor">
      <thead>
        <tr>
          <th>{{ sagdMode ? "BMB" : "SENSOR" }}</th>
          <th v-if="sagdMode">
            {{
              format(
                convert(
                  BMB_WEIGHT_SAGD_LB,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th v-else>
            {{
              format(
                convert(
                  scenario.equipment?.r_sensor?.weight_pounds,
                  MassUnits.Pounds,
                  getMassUnit("Weight.convert.lbs").value
                )
              )
            }}
            {{ getMassUnit("Weight.convert.lbs").value?.shortLabel }}
          </th>
          <th v-if="sagdMode">
            {{
              format(
                convert(
                  BMB_LENGTH_SAGD_FEET,
                  LengthUnits.Feet,
                  getLargeLengthUnit("Length.convert.Feet").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.convert.Feet").value?.shortLabel }}
          </th>
          <th v-else>
            {{
              format(
                convert(
                  scenario.equipment?.r_sensor?.length_inches ?? 0,
                  LengthUnits.Inches,
                  getLargeLengthUnit("Length.convert.Inches").value
                )
              )
            }}
            {{ getLargeLengthUnit("Length.convert.Inches").value?.shortLabel }}
          </th>
          <th>
            <span style="font-size: xx-small">MD</span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="sagdMode">
          <td>Summit 538 Series BMB / Filter</td>
          <td></td>
          <td></td>
          <td>
            {{
              formatDepth(
                scenarioCalculations.equipmentLengths.bottomOfEquipmentDepthFt
                  .value
              )
            }}
          </td>
        </tr>
        <tr v-else-if="scenario.equipment?.r_sensor">
          <td>
            {{ computeSensorLabel(scenario.equipment?.r_sensor) }}
          </td>
          <td></td>
          <td></td>
          <td>
            {{
              formatDepth(
                scenarioCalculations.equipmentLengths.bottomOfEquipmentDepthFt
                  .value
              )
            }}
          </td>
        </tr>
        <tr v-else>
          <td><em>N/A</em></td>
          <td></td>
          <td></td>
          <td></td>
        </tr>
      </tbody>
    </table>
    <!-- #endregion sensor-box -->
  </div>
</template>

<script setup lang="ts">
import type { Scenario } from "@/composables/forms/formTypes";
import type { ScenarioCalculations } from "@/composables/scenarioCalculations/calculationTypes";
import { computePumpLabel } from "@/models/equipment/pumps/properties";
import { computeIntakeLabel } from "@/models/equipment/intake/properties";
import { computeSealLabel } from "@/models/equipment/seals/properties";
import { computeMotorLabel } from "@/models/equipment/motors/properties";
import { computeSensorLabel } from "@/models/equipment/sensors/properties";
import { computeYToolLabel } from "@/models/equipment/y_tool/properties";
import {
  computeBoltOnDischargeLabel,
  isBoltOnDischargeMeasurementUnknown
} from "@/models/equipment/bolt_on_discharges/properties";
import { computeTubingLabel } from "@/models/equipment/tubings/properties";
import { computeCasingLabel } from "@/models/equipment/casings/properties";

import { computed, toRefs } from "vue";
import type { ComputedRef } from "vue";
import { convert } from "@/libs/units";
import { useScenarioUnits } from "@/composables/units/useScenarioUnits";
import { LengthUnits } from "@/libs/units/length";
import {
  BMB_LENGTH_SAGD_FEET,
  BMB_WEIGHT_SAGD_LB
} from "@/libs/calculations/constants";

import { useSettingsStore } from "@/store";
import { MassUnits } from "@/libs/units/mass";

import { sizingKey } from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { injectStrict } from "@/utils/utils";

const props = defineProps<{
  scenario: Scenario;
  scenarioCalculations: ScenarioCalculations;
  sagdMode?: boolean;
}>();
const { userSettingsNumberFormat } = useSettingsStore();

const format = Intl.NumberFormat(userSettingsNumberFormat.value, {
  maximumFractionDigits: 2
}).format;

const motorStats = computed(() => [
  {
    field: "Horsepower",
    nameplate: props.scenarioCalculations.motorCalculations?.hpNameplate.value,
    rerate: format(
      props.scenarioCalculations.motorCalculations?.adjustedReratedNameplateHP
        ?.value ?? 0
    )
  },
  {
    field: "Voltage",
    nameplate:
      props.scenarioCalculations.motorCalculations?.voltageNameplate.value,
    rerate: format(
      Math.round(
        props.scenarioCalculations.motorCalculations?.adjustedReratedNameplateV
          ?.value ?? 0
      )
    )
  },
  {
    field: "Amperage",
    nameplate:
      props.scenarioCalculations.motorCalculations?.amperageNameplate.value,
    rerate: format(
      props.scenarioCalculations.motorCalculations?.adjustedReratedNameplateA
        .value ?? 0
    )
  }
]);

const { scenario } = toRefs(props);

const sizing = injectStrict(sizingKey, null);
const { getLargeLengthUnit, getMassUnit } = useScenarioUnits(scenario, sizing);

const formatDepth = (depthFt: number) =>
  `${format(
    convert(
      depthFt,
      LengthUnits.Feet,
      getLargeLengthUnit("Equipment Length.convert.Feet").value
    )
  )} ${getLargeLengthUnit("Equipment Length.convert.Feet").value?.shortLabel}`;

// Given the depth at the top of a stack and each item's length (in feet, in
// stacking order), returns the running bottom-of-item MD for each item.
const cumulativeDepthsFt = (
  startDepthFt: number,
  itemLengthsFt: number[]
): number[] => {
  let depthFt = startDepthFt;
  return itemLengthsFt.map(lengthFt => (depthFt += lengthFt ?? 0));
};

type PumpTableData = {
  housingIndex: number;
  pumpLabel: string;
  length: string;
  lengthLabel: string;
  weight: string;
  weightLabel: string;
  bottomDepthFt: number;
};

const pumpTableData: ComputedRef<PumpTableData[]> = computed(() => {
  let bottomDepthFt =
    props.scenarioCalculations.equipmentLengths.completePumpDepthFt.value;

  return props.scenarioCalculations.descendingPumpsWithHousingIndex.value.map(
    ({ pump: p, housing: h, housingIndex }) => {
      const lengthUnit = h ? `Length.convert.Inches` : `Pump Length.write.Feet`;
      const weightUnit = h ? "Weight.convert.lbs" : "Pump Weight.write.lbs";

      const rawLengthFt = h
        ? convert(h.length_inches, LengthUnits.Inches, LengthUnits.Feet)
        : p.length_feet ?? 0;

      const length = h
        ? convert(
            h.length_inches,
            LengthUnits.Inches,
            getLargeLengthUnit(lengthUnit).value
          )
        : convert(
            p.length_feet,
            LengthUnits.Feet,
            getLargeLengthUnit(lengthUnit).value
          );

      const weight = h
        ? convert(
            h.r_housing?.weight_pounds,
            MassUnits.Pounds,
            getMassUnit(weightUnit).value
          )
        : convert(
            p.weight_pounds,
            MassUnits.Pounds,
            getMassUnit(weightUnit).value
          );

      const pumpLabel = computePumpLabel(p.r_pump, p.use_high_strength_shaft);
      const pumpLabelPrefix = h
        ? `${h.r_housing?.housing_number} / ${h.stage_count}`
        : `GENERIC HOUSING`;

      bottomDepthFt += rawLengthFt;

      return {
        housingIndex,
        pumpLabel: `${pumpLabelPrefix} (${pumpLabel})`,
        length: format(length),
        lengthLabel: getLargeLengthUnit(lengthUnit).value?.shortLabel,
        weight: format(weight),
        weightLabel: getMassUnit(weightUnit).value.shortLabel,
        bottomDepthFt
      } as PumpTableData;
    }
  );
});

const intakeDepthsFt = computed(() =>
  cumulativeDepthsFt(
    props.scenarioCalculations.equipmentLengths.bottomOfPumpsDepthFt.value,
    props.scenarioCalculations.orderedIntakes.value.map(n =>
      convert(n.r_intake?.length_inches, LengthUnits.Inches, LengthUnits.Feet)
    )
  )
);

const sealDepthsFt = computed(() =>
  cumulativeDepthsFt(
    props.scenarioCalculations.equipmentLengths.bottomOfIntakesDepthFt.value,
    props.scenarioCalculations.descendingOrderSeals.value.map(s =>
      convert(s.r_seal?.length_inches, LengthUnits.Inches, LengthUnits.Feet)
    )
  )
);

const cableDepthsFt = computed(() =>
  cumulativeDepthsFt(
    0,
    props.scenarioCalculations.orderedCables.value.map(c => c.length_feet ?? 0)
  )
);

const bypassTubingDepthsFt = computed(() =>
  cumulativeDepthsFt(
    props.scenarioCalculations.equipmentLengths.bottomOfYToolDepthFt.value,
    props.scenarioCalculations.orderedYToolBypassTubings.value.map(
      t => t.length_feet ?? 0
    )
  )
);

const equipmentTubingDepthsFt = computed(() =>
  cumulativeDepthsFt(
    props.scenarioCalculations.equipmentLengths.bottomOfYToolDepthFt.value,
    props.scenarioCalculations.orderedYToolEquipmentTubings.value.map(
      t => t.length_feet ?? 0
    )
  )
);

const ytoolTotalWeight = computed(() => {
  const ytoolWeight =
    scenario.value?.equipment?.r_uphole_y_tool?.weight_pounds ?? 0;

  const bypassWeight =
    props.scenarioCalculations.orderedYToolBypassTubings.value.reduce(
      (total, tubing) =>
        total +
        (tubing.r_tubing?.pound_per_foot ?? 0) * (tubing.length_feet ?? 0),
      0
    );

  const equipmentWeight =
    props.scenarioCalculations.orderedYToolEquipmentTubings.value.reduce(
      (total, tubing) =>
        total +
        (tubing.r_tubing?.pound_per_foot ?? 0) * (tubing.length_feet ?? 0),
      0
    );

  return ytoolWeight + bypassWeight + equipmentWeight;
});

const tubingSubtotalWeight = computed(() => {
  const topTubingWeight =
    (scenario.value?.well_properties?.r_top_tubing?.pound_per_foot ?? 0) *
    (scenario.value?.well_properties?.top_tubing_length_feet ?? 0);

  const bottomTubingWeight =
    (scenario.value?.well_properties?.r_bottom_tubing?.pound_per_foot ?? 0) *
    (scenario.value?.well_properties?.bottom_tubing_length_feet ?? 0);

  return topTubingWeight + bottomTubingWeight;
});

const tubingSubtotalLength = computed(() => {
  const topTubingLength =
    scenario.value?.well_properties?.top_tubing_length_feet ?? 0;

  const bottomTubingLength =
    scenario.value?.well_properties?.bottom_tubing_length_feet ?? 0;

  return topTubingLength + bottomTubingLength;
});
</script>

<style lang="scss">
.info-box {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;

  thead {
    tr {
      background: var(--surface-hover);
      font-weight: 800;
      font-family: var(--monospace-family);
      th {
        text-align: right;
        font-size: 9pt;
        padding: 0.25em;
      }
      th:nth-child(1) {
        text-align: left;
        width: 48%;
      }
      th:nth-child(2),
      th:nth-child(3),
      th:nth-child(4) {
        width: 29%;
        text-align: right;
        white-space: nowrap;
        font-family: var(--monospace-family);
      }

      th:nth-child(3) {
        width: 26%;
      }

      th:nth-child(4) {
        width: 37%;
      }
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid var(--gray-200);
    }

    th {
      text-align: left;
      width: 20%;
      padding: 0.25em;
    }

    td {
      vertical-align: top;
      padding: 0.25em;

      &:nth-child(1) {
        width: 48%;
      }

      &:nth-child(2),
      &:nth-child(3),
      &:nth-child(4) {
        width: 29%;
        text-align: right;
        font-family: var(--monospace-family);
      }
      &:nth-child(3) {
        width: 26%;
      }

      &:nth-child(4) {
        width: 37%;
      }
    }
  }
}

.info-box-flex {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100%;
  padding-bottom: 4em;
  gap: 16pt;
}
</style>
